import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { createRole, updateRole } from "../../../Api/RbacAdminApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";

const CreateEditRoleDialog = ({ open, mode, role, onClose, onSuccess }) => {
  const isEdit = mode === "edit";
  const { openNotification } = useToast();

  const [form, setForm] = useState({ roleCode: "", roleName: "", roleDescription: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (isEdit && role) {
        setForm({
          roleCode: role.roleCode || "",
          roleName: role.roleName || "",
          roleDescription: role.roleDescription || "",
        });
      } else {
        setForm({ roleCode: "", roleName: "", roleDescription: "" });
      }
    }
  }, [open, isEdit, role]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await updateRole(role.id, {
          roleName: form.roleName,
          roleDescription: form.roleDescription,
        });
      } else {
        res = await createRole({
          roleCode: form.roleCode,
          roleName: form.roleName,
          roleDescription: form.roleDescription,
        });
      }
      if (res?.responseStatus?.code === CODE.SUCCESS) {
        openNotification({
          text: isEdit ? "Role updated successfully" : "Role created successfully",
          icon: "toast-success",
        });
        onSuccess();
      } else {
        openNotification({
          text: res?.responseStatus?.msg || (isEdit ? "Failed to update role" : "Failed to create role"),
          icon: "toast-error",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const isSaveDisabled =
    saving || !form.roleName.trim() || (!isEdit && !form.roleCode.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 20, fontWeight: 700, pb: 1 }}>
        {isEdit ? "Edit Role" : "Create Role"}
      </DialogTitle>
      <DialogContent sx={{ pt: "16px !important" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {!isEdit && (
            <Box>
              <TextField
                label="Role Code"
                value={form.roleCode}
                onChange={(e) => setForm({ ...form, roleCode: e.target.value })}
                fullWidth
                required
                inputProps={{ style: { fontSize: 15 } }}
                InputLabelProps={{ style: { fontSize: 15 } }}
              />
              <Typography sx={{ fontSize: 13, color: "#888", mt: 0.75, ml: 0.25 }}>
                Unique identifier (e.g. FLEET_MANAGER). Cannot be changed after creation.
              </Typography>
            </Box>
          )}
          <TextField
            label="Role Name"
            value={form.roleName}
            onChange={(e) => setForm({ ...form, roleName: e.target.value })}
            fullWidth
            required
            inputProps={{ style: { fontSize: 15 } }}
            InputLabelProps={{ style: { fontSize: 15 } }}
          />
          <TextField
            label="Description"
            value={form.roleDescription}
            onChange={(e) => setForm({ ...form, roleDescription: e.target.value })}
            fullWidth
            multiline
            rows={3}
            inputProps={{ style: { fontSize: 15 } }}
            InputLabelProps={{ style: { fontSize: 15 } }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} disabled={saving} sx={{ fontSize: 14 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled}
          sx={{ fontSize: 14, minWidth: 90 }}
        >
          {saving ? <CircularProgress size={18} /> : isEdit ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEditRoleDialog;
